import { PROVENANCE_COPY, MONTH_NAMES } from "./provenance.constants.ts";
import {
  clampDateParts,
  daysInMonth,
  formatDateParts,
  parseDateParts,
  yearOptions,
  type DateParts,
} from "./provenance.model.ts";

/**
 * A date as three selects, and the only way to put one on an entry.
 *
 * Used twice, for start and for end, so the two ends of a range cannot drift
 * into different formats — which is what a pair of free-text boxes let happen.
 *
 * THE CASCADE IS THE VALIDATION. Month is unreachable without a year and day is
 * unreachable without a month, so a half-formed date has no path to exist and
 * no error message needs to describe one. What that leaves representable is
 * exactly what a provenance chain honestly knows: a year, a year and a month, a
 * full date, or nothing at all.
 */
export function ProvenanceDateField({
  id,
  label,
  value,
  help,
  currentYear,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  help?: string;
  /** Overridable so a test can pin the year list instead of mocking a clock. */
  currentYear?: number;
  onChange: (next: string) => void;
}) {
  const parts = parseDateParts(value);
  const helpId = `${id}-help`;

  const set = (next: DateParts) => {
    onChange(formatDateParts(clampDateParts(next)));
  };

  const days = Array.from({ length: daysInMonth(parts.month, parts.year) }, (_, i) =>
    String(i + 1),
  );

  return (
    <div className="provenance__field">
      {/* Points at the year, because the year is the part that carries the
          date — the other two are disabled until it has one. */}
      <label htmlFor={`${id}-year`}>{label}</label>

      <div className="provenance__date">
        <select
          id={`${id}-day`}
          className="provenance__date-day"
          aria-label={`${label} — ${PROVENANCE_COPY.dateDay}`}
          value={parts.day}
          disabled={parts.month === ""}
          onChange={(event) => set({ ...parts, day: event.target.value })}
        >
          <option value="">{PROVENANCE_COPY.dateBlank}</option>
          {days.map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </select>

        <select
          id={`${id}-month`}
          className="provenance__date-month"
          aria-label={`${label} — ${PROVENANCE_COPY.dateMonth}`}
          value={parts.month}
          disabled={parts.year === ""}
          onChange={(event) => set({ ...parts, month: event.target.value })}
        >
          <option value="">{PROVENANCE_COPY.dateBlank}</option>
          {MONTH_NAMES.map((month) => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </select>

        <select
          id={`${id}-year`}
          className="provenance__date-year"
          aria-describedby={help ? helpId : undefined}
          value={parts.year}
          onChange={(event) => set({ ...parts, year: event.target.value })}
        >
          <option value="">{PROVENANCE_COPY.dateBlank}</option>
          {yearOptions(currentYear).map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {help ? (
        <p className="provenance__help" id={helpId}>
          {help}
        </p>
      ) : null}
    </div>
  );
}
